import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import AppDateTimePicker from 'src/common/components/form/AppDateTimePicker';
import EditorDialogShell from 'src/common/components/modals/EditorDialogShell';
import dayjs from 'src/helpers/dayjs';
export interface BulkActionOptions {
  skipConfirm?: boolean;
}

interface Props {
  open: boolean;
  handleClose: () => void;
  participantCount: number;
  onStartAll: (options?: BulkActionOptions) => void;
  onSetAllStartTimes: (timeStamp: Date, options?: BulkActionOptions) => void;
  onStopAll: (options?: BulkActionOptions) => void;
  onSetAllFinishTimes: (timeStamp: Date, options?: BulkActionOptions) => void;
  onResetAllTimers: (options?: BulkActionOptions) => void;
}

const CheckinBulkActionsModal = ({
  open,
  handleClose,
  participantCount,
  onStartAll,
  onSetAllStartTimes,
  onStopAll,
  onSetAllFinishTimes,
  onResetAllTimers,
}: Props) => {
  const { t } = useTranslation();
  const [skipConfirm, setSkipConfirm] = useState(false);
  const [startTimeValue, setStartTimeValue] = useState(dayjs());
  const [finishTimeValue, setFinishTimeValue] = useState(dayjs());

  useEffect(() => {
    if (!open) {
      setSkipConfirm(false);
      return;
    }
    setSkipConfirm(false);
    const now = dayjs();
    setStartTimeValue(now);
    setFinishTimeValue(now);
  }, [open]);

  const options = (): BulkActionOptions => ({ skipConfirm });

  const hasParticipants = participantCount > 0;

  return (
    <EditorDialogShell
      open={open}
      onClose={handleClose}
      id="checkin-bulk-actions"
      title={t('timers.bulkActionsTitle')}
      maxWidth="sm"
    >
      <Stack spacing={3}>
        {hasParticipants ? (
          <Typography variant="body2" color="text.secondary">
            {t('timers.bulkActionsAppliesTo', { count: participantCount })}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('timers.bulkActionsNoParticipants')}
          </Typography>
        )}

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {t('timers.bulkActionsTimersSection')}
          </Typography>
          <Stack spacing={2.5}>
            <Button
              variant="contained"
              fullWidth
              disabled={!hasParticipants}
              onClick={() => onStartAll(options())}
            >
              {t('timers.startAllTimers')}
            </Button>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ alignItems: 'stretch' }}
            >
              <AppDateTimePicker
                ampm={false}
                openTo="hours"
                label={t('timers.editAllStartTimers')}
                value={startTimeValue}
                onChange={newValue => setStartTimeValue(newValue ?? dayjs())}
                disabled={!hasParticipants}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
              <Button
                variant="outlined"
                disabled={!hasParticipants}
                onClick={() =>
                  onSetAllStartTimes((startTimeValue.toDate() as Date) || new Date(), options())
                }
                sx={{ flexShrink: 0, minWidth: { sm: 100 } }}
              >
                {t('timers.applyStartTime')}
              </Button>
            </Stack>

            <Button
              variant="contained"
              fullWidth
              disabled={!hasParticipants}
              onClick={() => onStopAll(options())}
            >
              {t('timers.stopAllTimers')}
            </Button>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ alignItems: 'stretch' }}
            >
              <AppDateTimePicker
                ampm={false}
                openTo="hours"
                label={t('timers.editAllFinishTimers')}
                value={finishTimeValue}
                onChange={newValue => setFinishTimeValue(newValue ?? dayjs())}
                disabled={!hasParticipants}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
              <Button
                variant="outlined"
                disabled={!hasParticipants}
                onClick={() =>
                  onSetAllFinishTimes((finishTimeValue.toDate() as Date) || new Date(), options())
                }
                sx={{ flexShrink: 0, minWidth: { sm: 100 } }}
              >
                {t('timers.applyFinishTime')}
              </Button>
            </Stack>

            <Button
              variant="outlined"
              color="error"
              fullWidth
              disabled={!hasParticipants}
              onClick={() => onResetAllTimers(options())}
            >
              {t('timers.resetAllTimers')}
            </Button>
          </Stack>
        </Box>

        <FormControlLabel
          sx={{
            alignItems: 'flex-start',
            mx: 0,
            pt: 0.5,
            gap: 0.5,
            '& .MuiCheckbox-root': {
              p: 0.5,
              mt: '1px',
              alignSelf: 'flex-start',
            },
            '& .MuiFormControlLabel-label': {
              typography: 'body2',
              lineHeight: 1.5,
              pt: '6px',
            },
          }}
          control={
            <Checkbox
              size="small"
              checked={skipConfirm}
              onChange={e => setSkipConfirm(e.target.checked)}
            />
          }
          label={t('timers.confirmBulkAction')}
        />
      </Stack>
    </EditorDialogShell>
  );
};

export default CheckinBulkActionsModal;
